import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import { RxOpenInNewWindow } from "react-icons/rx";
import { FavoriteRegionStarButton } from "./favorite-region-star-button";
import { DataRanges } from "src/utils/configs";

/** levelLabel("시도"|"군구"|"동") → DataRanges 코드("S"|"G"|"D") */
const levelLabelToDataRange = (levelLabel) => {
  if (levelLabel === "시도") return DataRanges.SIDO;
  if (levelLabel === "군구") return DataRanges.GUNGU;
  if (levelLabel === "동") return DataRanges.DONG;
  return null;
};

/** 별표 다이얼로그의 parentName으로 넘길 값 — 군구는 시도명, 동은 "시도 군구" */
const buildParentName = (region) => {
  if (region.levelLabel === "군구") return region.parentSidoName ?? null;
  if (region.levelLabel === "동") {
    const parts = [region.parentSidoName, region.parentGunguName].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  }
  return null;
};

/** 변동값 표시 */
const ChangeValue = ({ value, unit = "", invert = false }) => {
  if (value == null) return <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>-</Typography>;
  const isPositive = value > 0;
  const color = invert ? (isPositive ? "#1565C0" : "#E53935") : (isPositive ? "#E53935" : "#1565C0");
  const arrow = isPositive ? "▲" : "▼";
  const display = Math.abs(value);
  return (
    <Typography variant="caption" sx={{ fontWeight: 600, color, fontSize: 11 }}>
      {arrow}{unit === "%" ? `${display.toFixed(1)}%` : `${Math.round(display).toLocaleString()}${unit}`}
    </Typography>
  );
};

/** 가격 포맷 (만원 단위) */
const formatStatPrice = (v) => {
  if (v == null) return "-";
  if (v >= 10000) return `${(v / 10000).toFixed(0)}억`;
  return `${Math.round(v).toLocaleString()}만`;
};

/** 짧은 날짜 라벨 */
const formatShortDateLabel = (dateValue) => {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** stat 항목으로 날짜 라벨 쌍 생성 (주간/월간 구분) */
const getDateLabels = (item) => {
  if (!item) return { prevLabel: null, curLabel: null };
  const pd = item.prevDate ? new Date(item.prevDate) : null;
  const cd = item.dataDate ? new Date(item.dataDate) : null;
  if (!cd) return { prevLabel: null, curLabel: null };
  const isWeekly = pd && Math.round((cd - pd) / (1000 * 60 * 60 * 24)) <= 14;
  if (isWeekly) {
    return {
      prevLabel: pd ? formatShortDateLabel(pd) : null,
      curLabel: formatShortDateLabel(cd),
    };
  }
  return {
    prevLabel: pd ? `${pd.getMonth() + 1}월` : null,
    curLabel: `${cd.getMonth() + 1}월`,
  };
};

/** 날짜별 from→to 인라인 표시 (날짜는 회색, 값만 검정 bold) */
const StatFromToInline = ({ stat, unit = "", format }) => {
  if (!stat) return null;
  const { prevLabel, curLabel } = getDateLabels(stat);
  const prev = stat.prevValue;
  const cur = stat.value;
  if (prev == null && cur == null) return null;
  const fmt = format || ((v) => v);
  return (
    <Typography component="span" variant="caption" sx={{ fontSize: 10 }}>
      {prevLabel && prev != null && (
        <>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{prevLabel} </Typography>
          <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{fmt(prev)}{unit}</Typography>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}> → </Typography>
        </>
      )}
      {curLabel && cur != null && (
        <>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{curLabel} </Typography>
          <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{fmt(cur)}{unit}</Typography>
        </>
      )}
    </Typography>
  );
};

/** 인구수 포맷 — "X.X만명" / "XXX명" */
const formatPopulation = (v) => {
  if (v == null || v === 0) return null;
  const n = Number(v);
  if (!isFinite(n)) return null;
  if (n >= 10000) {
    const man = n / 10000;
    return `${man >= 100 ? Math.round(man).toLocaleString() : man.toFixed(1)}만명`;
  }
  return `${Math.round(n).toLocaleString()}명`;
};

/**
 * 헤더 우측 메타 텍스트:
 * - 시도: 인구수
 * - 군구: 시도명 · 인구수
 * - 동: 시도명 군구명
 */
const buildHeaderMeta = (region) => {
  const pop = formatPopulation(region.population);
  if (region.levelLabel === "시도") return pop;
  if (region.levelLabel === "군구") {
    const parts = [];
    if (region.parentSidoName) parts.push(region.parentSidoName);
    if (pop) parts.push(pop);
    return parts.length > 0 ? parts.join(" · ") : null;
  }
  if (region.levelLabel === "동") {
    const parts = [];
    if (region.parentSidoName) parts.push(region.parentSidoName);
    if (region.parentGunguName) parts.push(region.parentGunguName);
    return parts.length > 0 ? parts.join(" ") : null;
  }
  return null;
};

/**
 * 관심 지역 요약 카드.
 * @param {object} region - 지역 데이터 (name, levelLabel, parentSidoName, parentGunguName, population, scStat, mpStat, rpcStat 등)
 * @param {function} onClick - 카드 클릭 핸들러
 * @param {function} onOpenNewWindow - 새창 버튼 클릭 핸들러 (없으면 버튼 숨김)
 * @param {boolean} hideTitle - true면 이름/링크/별표 행을 숨김 (다이얼로그 헤더 중복 방지)
 * @param {boolean} showStar - 별표 표시 여부 (기본 true). 다이얼로그 내부에선 false로 재귀 방지
 */
export const RegionSummaryCard = ({ region, onClick, onOpenNewWindow, chart = null, fullWidth = false, hideTitle = false, showStar = true }) => (
  <Box
    onClick={onClick}
    sx={{
      ...(fullWidth ? { width: "100%" } : { minWidth: 280, maxWidth: 350 }),
      p: 1.2,
      border: 1, borderColor: "divider", borderRadius: 1.5,
      cursor: onClick ? "pointer" : "default", flexShrink: 0,
      "&:hover": onClick ? { bgcolor: "action.hover", borderColor: "primary.main" } : {},
      transition: "all 0.15s",
    }}
  >
    {!hideTitle && (
    <Stack direction="row" alignItems="center" sx={{ mb: 0.5, gap: 0.3, minWidth: 0 }}>
      {/* 이름 — 자연 폭, 길면 ellipsis. 버튼들이 이름 바로 옆에 인라인으로 붙도록 flex:1 제거 */}
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: 700,
          fontSize: 12,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {region.name}
      </Typography>
      {onOpenNewWindow && (
        <IconButton
          size="small"
          onClick={(e) => {
            // 카드 onClick으로 전파되지 않도록 차단 후 새창 열기
            e.stopPropagation();
            onOpenNewWindow(region);
          }}
          sx={{ p: 0.2, flexShrink: 0 }}
          aria-label="지역정보 새창으로 열기"
        >
          <RxOpenInNewWindow size={12} />
        </IconButton>
      )}
      {/* 관심지역 별표 — 다이얼로그 내부에선 showStar=false로 재귀 방지 */}
      {showStar && (() => {
        const dataRange = levelLabelToDataRange(region.levelLabel);
        if (!dataRange || !region.id) return null;
        return (
          <Box sx={{ flexShrink: 0 }}>
            <FavoriteRegionStarButton
              regionId={region.id}
              regionName={region.name}
              dataRange={dataRange}
              parentName={buildParentName(region)}
              iconSize={12}
            />
          </Box>
        );
      })()}
      {/* 메타 (시도명/인구수) — 버튼 뒤에 회색 보조 텍스트로. 공간 부족 시 줄어듦 */}
      {(() => {
        const meta = buildHeaderMeta(region);
        return meta ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontSize: 9, fontWeight: 400, ml: 0.3, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {meta}
          </Typography>
        ) : null;
      })()}
    </Stack>
    )}

    {!hideTitle && <Divider sx={{ my: 0.5 }} />}

    {/* 매매시세 */}
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.3 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>매매시세</Typography>
        <StatFromToInline stat={region.mpStat} format={formatStatPrice} />
      </Stack>
      <ChangeValue value={region.mpChangeRate} unit="%" />
    </Stack>

    {/* 매매거래량 */}
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.3 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>매매거래</Typography>
        {region.rpcStat?.prevValue != null && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
            (월평균 <Typography component="span" variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: "text.primary" }}>{Math.round(region.rpcStat.prevValue).toLocaleString()}</Typography>건)
          </Typography>
        )}
        {region.rpcStat?.value != null && region.rpcStat?.dataDate && (
          <Typography component="span" variant="caption" sx={{ fontSize: 10 }}>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{new Date(region.rpcStat.dataDate).getMonth() + 1}월 </Typography>
            <Typography component="span" variant="caption" sx={{ fontSize: 10, fontWeight: 700, color: "text.primary" }}>{Math.round(region.rpcStat.value).toLocaleString()}건</Typography>
          </Typography>
        )}
      </Stack>
      <ChangeValue value={region.rpcChangeValue} unit="건" />
    </Stack>

    {/* 매매매물수 */}
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.3 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>매매매물</Typography>
        <StatFromToInline stat={region.scStat} unit="건" format={(v) => Math.round(v).toLocaleString()} />
      </Stack>
      <ChangeValue value={region.scChangeRate} unit="%" invert />
    </Stack>

    {/* 미니 차트 (매매/전세 지수 or 시세) */}
    {chart}
  </Box>
);
